import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray, AbstractControl, ValidationErrors } from '@angular/forms';
import { VesselCalculationInventoryService } from 'src/app/services/danish-shipping-services/vessel-calculation-inventory.service';
import { ToastrService } from 'ngx-toastr';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { VesselApiService } from 'src/app/services/danish-shipping-services/vessel-api.service';

@Component({
  selector: 'app-vessel-subgroup-create',
  templateUrl: './vessel-subgroup-create.component.html',
  styleUrls: ['./vessel-subgroup-create.component.css']
})
export class VesselSubgroupCreateComponent implements OnInit {
  vesselSubgroupForm: FormGroup;
  isSubmitting = false;
  errors: any = {};
  vesselTypes: string[] = [];
  selectedItem = '';
  lifeTimeOptions: number[] = [15, 20, 25, 30, 35, 40];


  sfiDescriptions: { [key: number]: string } = {
    2: 'Hull Structure',
    3: 'Equipment for Cargo',
    4: 'Ship Equipment',
    5: 'Equipment for Crew and passengers',
    6: 'Machinery Main Components',
    7: 'Systems for Machinery Main Components',
    8: 'Ship Common Systems',
  };

  constructor(
    private fb: FormBuilder,
    private vesselService: VesselCalculationInventoryService,
    private vesselTypeService: VesselApiService,
    private toastr: ToastrService,
    private router: Router
  ) {
    this.vesselSubgroupForm = this.fb.group({
      vesselType: ['', Validators.required],
      subType: ['', Validators.required],
      NewLifeTime: [null, Validators.required],
      conversionFactor: [null, Validators.required],
      subgroups: this.fb.array([], this.validateWeightDistributionSum.bind(this))
    });
  }

  ngOnInit(): void {
    this.loadVesselTypes();

    for (let i = 1; i < 8; i++) {
      this.addSubgroup();
    }
    this.addAssemblySubgroup();
  }

  addAssemblySubgroup(): void {
    const subgroupForm = this.fb.group({
      sfiCode: [{ value: '', disabled: true }],
      description: [{ value: 'Assembly', disabled: true }],
      coefficient: ['', Validators.required],
      weightDistribution: [{ value: '', disabled: true }],
      isAssembly: [true]
    });

    this.subgroups.push(subgroupForm);
  }

  loadVesselTypes(): void {
    this.vesselTypeService.getVesselTypes().subscribe({
      next: types => {
        this.vesselTypes = types || [];
        this.selectedItem = '';
      },
      error: err => console.error('Error fetching vessel types', err)
    });
  }

  selectVesselType(type: string): void {
    this.selectedItem = type;
    this.vesselSubgroupForm.get('vesselType')?.setValue(type);
  }

  get subgroups(): FormArray {
    return this.vesselSubgroupForm.get('subgroups') as FormArray;
  }

  addSubgroup(): void {
    const index = this.subgroups.length + 2;
    const subgroupForm = this.fb.group({
      sfiCode: [{ value: index, disabled: true }, Validators.required],
      description: [{ value: this.sfiDescriptions[index] || '', disabled: true }],
      coefficient: [null, [Validators.required, Validators.pattern(/^\d+(\.\d{1,10})?$/)]],
      weightDistribution: [null, [Validators.required, Validators.min(0), Validators.max(100)]],
    });
    this.subgroups.push(subgroupForm);
  }

  removeSubgroup(index: number): void {
    this.subgroups.removeAt(index);
  }

createVesselSubgroups(): void {
  if (this.isSubmitting) return;

  this.isSubmitting = true;
  this.errors = {};

  if (this.vesselSubgroupForm.invalid) {
    this.toastr.error('Please fill all required fields and Total weight distribution cannot exceed 100%.');
    this.isSubmitting = false;
    return;
  }

  const rawValue = this.vesselSubgroupForm.getRawValue();

rawValue.subgroups = rawValue.subgroups.map((subgroup: any) => {
  if (subgroup.isAssembly) {
    if (subgroup.sfiCode === '' || subgroup.sfiCode == null) {
      subgroup.sfiCode = 0;
    }

    if (subgroup.weightDistribution === '' || subgroup.weightDistribution == null) {
      subgroup.weightDistribution = 0;
    }
  }
  return subgroup;
});


  this.vesselService.createVesselSubgroups(rawValue)
    .pipe(
      catchError(error => {
        return [];
      })
    )
    .subscribe(response => {
      this.toastr.success('Vessel subgroups created successfully!');
      this.vesselSubgroupForm.reset();
      while (this.subgroups.length !== 0) {
        this.subgroups.removeAt(0);
      }
      this.addSubgroup();

      this.router.navigate(['/homeAdmin/v2/vesselSubgroup']);
    }, error => {
      console.error('Error creating vessel subgroups:', error);
    }).add(() => {
      this.isSubmitting = false;
    });
}

isAnyWeightDistributionTouched(): boolean {
  return this.subgroups.controls.some(control =>
    control.get('weightDistribution')?.touched
  );
}

validateWeightDistributionSum(formArray: AbstractControl): ValidationErrors | null {
  const subgroups = formArray.value;
  if (!subgroups || subgroups.length === 0) return null;

  const totalWeight = subgroups.reduce((sum: number, subgroup: any) => {
    if (subgroup.isAssembly) return sum; 
    const weight = parseFloat(subgroup.weightDistribution);
    return sum + (isNaN(weight) ? 0 : weight);
  }, 0);

  return totalWeight !== 100 ? { weightDistributionSumNot100: true } : null;
}
get weightDistributionTotal(): number {
  let total = 0;

  this.subgroups.controls.forEach(control => {
    const isAssembly = control.get('isAssembly')?.value;
    if (!isAssembly) {
      const value = parseFloat(control.get('weightDistribution')?.value);
      if (!isNaN(value)) total += value;
    }
  });

  return Math.round(total * 100) / 100;
}

get isWeightDistributionValid(): boolean {
  const total = this.weightDistributionTotal;
  const allValid = this.subgroups.controls.every(control => {
    const isAssembly = control.get('isAssembly')?.value;
    if (isAssembly) return true;

    const value = parseFloat(control.get('weightDistribution')?.value);
    return !isNaN(value) && value > 0;
  });

  return total === 100 && allValid;
}

}
